import { useEffect, useState } from "react";
import { track } from "@/lib/track";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { composePhone, parsePhone } from "@/lib/countryDialCodes";
import type { ContactFieldKey } from "@/lib/profileCompleteness";
import { toast } from "sonner";
import { UserRound, Loader2 } from "lucide-react";

// Quick-complete gate for contacting owners. When a renter tries to message a
// landlord with required profile fields missing (per-tenant set from the admin
// Quality control panel), this collects ONLY the missing ones on the spot,
// saves them (marking the profile complete), and hands control back so the
// caller can send the original message immediately, no redirect, nothing lost.

export interface CompletedProfileValues {
  displayName: string;
  phone: string;
  introduction: string;
}

interface CompleteProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which required fields are missing, only these render. */
  missing: ContactFieldKey[];
  /** Current values, used to prefill and to return a full snapshot. */
  initialValues: CompletedProfileValues;
  /** Fires after a successful save (dialog already closed). */
  onCompleted: (saved: CompletedProfileValues) => void;
  submitLabel?: string;
  /** z-index overrides so the dialog can sit above the concierge overlay. */
  overlayClassName?: string;
  contentClassName?: string;
}

const CompleteProfileDialog = ({
  open,
  onOpenChange,
  missing,
  initialValues,
  onCompleted,
  submitLabel = "Save & send message",
  overlayClassName,
  contentClassName,
}: CompleteProfileDialogProps) => {
  const { user } = useAuth();
  const tenant = useTenant();

  const [displayName, setDisplayName] = useState("");
  const [phoneIso, setPhoneIso] = useState(tenant.country || "CR");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-prefill each time the dialog opens (values may have changed since).
  useEffect(() => {
    if (!open) return;
    setDisplayName(initialValues.displayName ?? "");
    setIntroduction(initialValues.introduction ?? "");
    const parsed = parsePhone(initialValues.phone ?? "");
    setPhoneNumber(parsed.number);
    setPhoneIso(parsed.number ? parsed.iso : tenant.country || parsed.iso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const asks = (f: ContactFieldKey) => missing.includes(f);

  const canSubmit =
    (!asks("name") || displayName.trim().length > 0) &&
    (!asks("phone") || phoneNumber.replace(/\D/g, "").length > 0) &&
    (!asks("introduction") || introduction.trim().length > 0);

  const handleSave = async () => {
    if (!user || !canSubmit || saving) return;
    setSaving(true);
    try {
      const composedPhone = composePhone(phoneIso, phoneNumber);

      // Only write the fields we actually asked for (never clobber the rest),
      // plus the completed flag, the ratchet: the required set is now met.
      const patch: Record<string, unknown> = { profile_completed: true };
      if (asks("name")) patch.display_name = displayName.trim();
      if (asks("introduction")) patch.introduction = introduction.trim();
      const { data: updated, error: profErr } = await supabase
        .from("profiles")
        .update(patch)
        .eq("user_id", user.id)
        .select("user_id");
      if (profErr) throw profErr;
      track("profile_completed", { surface: "contact-gate" });
      // Rare: the signup trigger failed to create the profiles row, the
      // update matches nothing and "succeeds" silently. Create it instead.
      if (!updated || updated.length === 0) {
        const { error: insErr } = await supabase
          .from("profiles")
          .insert({ user_id: user.id, ...patch } as never);
        if (insErr) throw insErr;
      }

      if (asks("phone")) {
        // Deliberately phone-only: upserting other profile_contacts columns
        // (e.g. pet_details) here would null out existing data.
        const { error: phoneErr } = await supabase
          .from("profile_contacts")
          .upsert({ user_id: user.id, phone: composedPhone }, { onConflict: "user_id" });
        if (phoneErr) throw phoneErr;
      }

      onOpenChange(false);
      onCompleted({
        displayName: asks("name") ? displayName.trim() : initialValues.displayName,
        phone: asks("phone") ? composedPhone : initialValues.phone,
        introduction: asks("introduction") ? introduction.trim() : initialValues.introduction,
      });
    } catch {
      toast.error("Couldn't save your details, please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-md ${contentClassName ?? ""}`} overlayClassName={overlayClassName}>
        <DialogHeader>
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
            <UserRound className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-serif text-xl">
            One quick step before you send
          </DialogTitle>
          <DialogDescription className="text-center">
            Owners see this with your request. Complete profiles get faster replies.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {asks("name") && (
            <div className="space-y-1.5">
              <Label htmlFor="cpd-name" className="text-sm font-medium">Your name</Label>
              <Input
                autoComplete="name"
                id="cpd-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Full name"
                maxLength={120}
                className="rounded-xl"
              />
            </div>
          )}
          {asks("phone") && (
            <div className="space-y-1.5">
              <Label htmlFor="cpd-phone" className="text-sm font-medium">WhatsApp number</Label>
              <PhoneNumberInput
                id="cpd-phone"
                countryIso={phoneIso}
                number={phoneNumber}
                onCountryChange={setPhoneIso}
                onNumberChange={setPhoneNumber}
              />
            </div>
          )}
          {asks("introduction") && (
            <div className="space-y-1.5">
              <Label htmlFor="cpd-intro" className="text-sm font-medium">Short introduction</Label>
              <Textarea
                id="cpd-intro"
                value={introduction}
                onChange={(e) => setIntroduction(e.target.value)}
                placeholder="Tell owners a little about yourself: who you are, why you're moving here…"
                rows={3}
                maxLength={600}
                className="rounded-xl resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2">
          <Button className="w-full" onClick={handleSave} disabled={!canSubmit || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteProfileDialog;
